import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Modal from "../components/Modal";
import { useNavigate } from "react-router-dom";
import Avatar from "react-avatar";
import { useAuth } from "../context/AuthContext";
import UserAddress from "../components/UserAddress";
import { seoDefaults } from "../utils/seoDefaults";
import SEO from "../api/seo";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SERVER_BASE_URL = BASE_URL.replace(/\/api$/, "");

const UserProfile = () => {
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [addresses, setAddresses] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    phone_number: "",
    status: "",
    avatar: "",
  });
  const [editFormErrors, setEditFormErrors] = useState({});
  const [errors, setErrors] = useState({
    phone_number: "",
  });

  useEffect(() => {
    const hasReloaded = sessionStorage.getItem("hasReloaded");

    if (!hasReloaded) {
      sessionStorage.setItem("hasReloaded", "true");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }, []);

  const fetchUserDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.warn(
          "No authentication token found in localStorage for fetchUserDetails."
        );
        throw new Error("Authentication required. Please log in.");
      }

      const url = `${BASE_URL}/user-details`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (error) {
      console.error(
        "Error in fetchUserDetails API:",
        error.response?.data || error.message || error
      );
      const errorMessage =
        error.response?.data?.message ||
        "Failed to fetch user details. Network error or server issue.";
      throw new Error(errorMessage);
    }
  };

  const updateUserDetails = async (userId, updatedData) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication required. Please log in.");
      }

      const url = `${BASE_URL}/user/${userId}`;

      const res = await axios.put(url, updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return res.data;
    } catch (error) {
      console.error(
        "Error in updateUserDetails API:",
        error.response?.data || error.message || error
      );
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update user details. Network error or server issue.";
      throw new Error(errorMessage);
    }
  };

  useEffect(() => {
    const loadUserData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchUserDetails();
        setUserData(data);
        setEditFormData({
          name: data.name || "",
          phone_number: data.phone_number || "",
          status: data.status || "",
          avatar: data.avatar || "",
        });
      } catch (err) {
        console.error("Error loading user data:", err);
        setError(
          err.message || "Failed to load user profile. Please try again."
        );
        setUserData(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, []);

  const handleEditClick = () => {
    if (userData) {
      setIsModalOpen(true);
      setEditFormData({
        name: userData.name || "",
        phone_number: userData.phone_number || "",
        status: userData.status || "",
        avatar: userData.avatar || "",
      });
      setEditFormErrors({});
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditFormErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Real-time phone validation
    if (name === "phone_number") {
      const phoneRegex = /^[6-9]\d{9}$/;
      if (!phoneRegex.test(value)) {
        setErrors((prev) => ({
          ...prev,
          phone_number: "Enter a valid 10-digit Indian number",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          phone_number: "",
        }));
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    window.location.reload();
  };

  const validateEditForm = () => {
    const errors = {};

    if (!editFormData.name.trim()) {
      errors.name = "Name is required.";
    }

    if (!editFormData.phone_number.trim()) {
      errors.phone_number = "Phone number is required.";
    } else if (!/^[6-9]\d{9}$/.test(editFormData.phone_number)) {
      errors.phone_number =
        "Phone number must be 10 digits (starting with 6–9).";
    }

    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateEditForm()) return;

    setIsLoading(true);
    try {
      if (!userData || !userData._id)
        throw new Error("User ID is missing for update.");

      const payload = {
        name: editFormData.name,
        phone_number: editFormData.phone_number,
        status: editFormData.status,
        avatar: editFormData.avatar,
      };

      await updateUserDetails(userData._id, payload);

      const refreshedUser = await fetchUserDetails();
      setUserData(refreshedUser);
      setIsModalOpen(false);
      window.location.reload();
    } catch (error) {
      const message =
      error?.response?.data?.message || error.message || "Update failed";
       if (message.includes("Phone number is already in use")) {
      setEditFormErrors({ phone_number: message });
    } else {
      setError(message);
    }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 text-white">
        <p>Loading user data...</p>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 text-white">
        <p>No user data available. Please ensure you are logged in.</p>
      </div>
    );
  }

  return (
    <>
      <SEO {...seoDefaults.profile} />
      <div className="min-h-screen bg-gray-950 text-white p-8 pt-20">
        <div className="max-w-2xl mx-auto mt-20 bg-gray-900 rounded-lg shadow-lg p-8">
          <h1 className="text-4xl font-bold text-center mb-8 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
            User Profile
          </h1>

          <div className="flex flex-col items-center mb-6">
            {/* <img
            src={
              userData.avatar ? `${SERVER_BASE_URL}${userData.avatar}` : avatar
            }
            alt="User Avatar"
            className="w-32 h-32 rounded-full object-cover border-4 border-purple-500"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = avatar;
            }} // Fallback for broken image URL
          /> */}

            <div className="w-36 h-36 rounded-full p-1 bg-gradient-to-br from-cyan-400 to-purple-600 shadow-lg">
              <div className="w-full h-full rounded-full overflow-hidden bg-gray-900 flex items-center justify-center">
                <Avatar
                  name={userData.name}
                  src={`${SERVER_BASE_URL}${userData.avatar}`}
                  size="128"
                  round={true}
                  color="#06b6d4"
                  fgColor="#ffffff"
                />
              </div>
            </div>

            <h2 className="text-3xl font-semibold mt-4">{userData.name}</h2>
            <p className="text-gray-400">{userData.email}</p>
          </div>

          <div className="space-y-4 text-gray-300">
            <div className="flex justify-between items-center border-b border-gray-700 pb-2">
              <span className="font-medium">Email:</span>
              <span>{userData.email}</span>
            </div>
            <div className="flex justify-between items-center border-b border-gray-700 pb-2">
              <span className="font-medium">Phone Number:</span>
              <span>{userData.phone_number}</span>
            </div>
            {/* <div className="flex justify-between items-center border-b border-gray-700 pb-2">
            <span className="font-medium">Status:</span>
            <span>{userData.status}</span>
          </div> */}
            <div className="flex justify-between items-center border-b border-gray-700 pb-2">
              <span className="font-medium">Account Created:</span>
              <span>
                {userData.createdAt
                  ? new Date(userData.createdAt).toLocaleDateString()
                  : "N/A"}
              </span>
            </div>
          </div>

          <UserAddress />

          <div className="flex justify-center mt-6 gap-4 flex-wrap">
            {/* Edit Profile Button */}
            <button
              onClick={handleEditClick}
              className="
      inline-flex items-center rounded-md border border-transparent
      bg-gradient-to-r from-cyan-500 to-purple-600 px-6 py-3 text-lg font-semibold text-white
      shadow-lg shadow-cyan-500/30 hover:from-cyan-600 hover:to-purple-700
      focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-gray-950
      transition-all duration-200 transform hover:scale-105
    "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2.5"
                stroke="currentColor"
                className="w-6 h-6 mr-2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                />
              </svg>
              Edit Profile
            </button>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="
      inline-flex items-center rounded-md border border-gray-600
      bg-gray-800 px-6 py-3 text-lg font-semibold text-white
      hover:bg-gray-700 hover:border-gray-500
      focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-950
      transition-all duration-200 transform hover:scale-105
    "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2.5"
                stroke="currentColor"
                className="w-6 h-6 mr-2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6A2.25 2.25 0 0 0 5.25 5.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3-3H9m9 0-3-3m3 3-3 3"
                />
              </svg>
              Logout
            </button>
          </div>
        </div>

        {/* Edit Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title="Edit Profile"
        >
          <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
            {" "}
            {/* Prevent default form submission */}
            {Object.keys(editFormErrors).length > 0 && (
              <div className="mb-2 p-2 rounded-lg bg-red-900 text-red-300 border border-red-700 text-xs">
                {Object.values(editFormErrors).map((err, index) => (
                  <p key={index}>{err}</p>
                ))}
              </div>
            )}
            <div>
              <label
                htmlFor="name"
                className="block mb-1 font-medium text-purple-300"
              >
                Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={editFormData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-md bg-gray-800 text-white border border-purple-700 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200"
              />
            </div>
            <div>
              <label
                htmlFor="phone_number"
                className="block mb-1 font-medium text-purple-300"
              >
                Phone Number
              </label>
              <input
                type="text"
                name="phone_number"
                id="phone_number"
                value={editFormData.phone_number}
                onChange={handleChange}
                className={`w-full px-3 py-2 rounded-md bg-gray-800 text-white border ${
                  errors.phone_number || editFormErrors.phone_number
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-purple-700 focus:ring-pink-500 focus:border-pink-500"
                } transition-all duration-200`}
              />
              {(editFormErrors.phone_number || errors.phone_number) && (
                <p className="text-red-500 text-xs mt-1">
                  {editFormErrors.phone_number || errors.phone_number}
                </p>
              )}
            </div>
            {/* <div>
            <label
              htmlFor="status"
              className="block mb-1 font-medium text-purple-300"
            >
              Status
            </label>
            <select
              disabled={true}
              name="status"
              id="status"
              value={editFormData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-md bg-gray-800 text-white border border-purple-700 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200 appearance-none cursor-not-allowed"
            >
              <option value="">Select Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div> */}
            <div className="flex justify-end space-x-2 pt-4">
              <button
                type="button"
                onClick={handleCloseModal}
                className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-md text-white transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpdate}
                disabled={isLoading}
                className="
                bg-green-600 hover:bg-green-700 px-4 py-2 rounded-md text-white transition-colors duration-200
                flex items-center justify-center
              "
              >
                {isLoading ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white mr-2"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </>
  );
};

export default UserProfile;
